import React, { useState, useEffect } from 'react';

import axios from 'axios';
import moment from 'moment-timezone';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';

import { Grid } from '@material-ui/core';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Multiselect } from 'react-widgets';
import 'react-widgets/styles.css';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './react-datepicker-override.css';

import { makeStyles, withStyles } from '@material-ui/core/styles';

import { Loading } from '../stories/Loading';
import PrimaryButton from './lib/buttons/PrimaryButton';
import SecondaryButton from './lib/buttons/SecondaryButton';

function toINR(number: any) {
  var x = number;
  x = x.toString();
  var afterPoint = '';
  if (x.indexOf('.') > 0) afterPoint = x.substring(x.indexOf('.'), x.length);
  x = Math.floor(x);
  x = x.toString();
  var lastThree = x.substring(x.length - 3);
  var otherNumbers = x.substring(0, x.length - 3);
  if (otherNumbers !== '') lastThree = ',' + lastThree;
  return (
    '\u20B9' +
    ' ' +
    otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') +
    lastThree +
    afterPoint
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  posTypeGroup: {
    display: 'flex',
    justifyContent: 'center',
    margin: '12px',
  },
  button: {
    maxWidth: '320px',
    width: '300%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignSelf: 'stretch',
    padding: '8px',
  },
  confirmButton: {
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  particularsTable: {
    marginTop: '12px',
    marginBottom: '12px',
    marginRight: '12px',
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
      alignSelf: 'center',
    },
  },
  invoiceDetailsContainer: {
    marginTop: '12px',
    marginBottom: '12px',
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  infoContainer: {
    flex: 1,
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
      marginLeft: '8px',
      marginRight: '8px',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
  },
  datePicker: {
    height: '24px',
    maxWidth: '140px',
    '&>div>input::active': {
      border: 'none',
    },
    '&>div>input': {
      height: '24px',
    },
  },
}));

const RestaurantInvoice = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [posTypes, setPosTypes] = useState<Map<string, string>>(new Map());

  const [billId, setBillId] = useState('');
  const [selectedPosType, setSelectedPosType] = useState('restaurant');
  const [einvInfo, setEinvInfo] = useState<any>();
  const [billInfo, setBillInfo] = useState<any>();
  const [billItemsInfo, setBillItemsInfo] = useState(new Array<any>());

  const [gstMappings, setGstMappings] = useState<[any]>();
  const [selectedCompany, setSelectedCompany] = useState<any>();
  const [partyHeldOnDateEnabled, setPartyHeldOnDateEnabled] = useState(false);
  const [partyHeldDate, setPartyHeldDate] = useState<Date | null>(null);

  const resetComponent = () => {
    setLoading(false);
    setBillId('');
    setSelectedPosType('restaurant');
    setBillInfo(undefined);
    setEinvInfo(undefined);
    setBillItemsInfo(new Array<any>());
    setSelectedCompany(undefined);

    setPartyHeldOnDateEnabled(false);
    setPartyHeldDate(null);
  };

  const downloadPosInvoice = async (billId: string) => {
    try {
      const invResult = await axios.get(`/api/pos/eInvoice?id=${billId}`, {
        responseType: 'blob',
      });
      const blob = invResult.data;
      var file = new Blob([blob], { type: 'application/pdf' });
      var url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      let fileName = `EINV-${billId}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      alert('Failed to download invoice. Please try again later!');
      console.log(error);
    }
  };

  const getAllGstMappings = () => {
    axios.get('/api/fetchGstMappings').then(({ data }) => {
      setGstMappings(data);
    });
  };

  const getSupportedPosTypes = () => {
    setLoading(true);

    axios
      .get('/api/pos/getSupportedTypes')
      .then((res) => res.data)
      .then((res) => {
        if (res.err) {
          alert(res.err);
        } else {
          setPosTypes(new Map(Object.entries(res)));
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getSupportedPosTypes();
    getAllGstMappings();
  }, []);

  const getBillInfo = () => {
    setLoading(true);
    axios
      .post('/api/pos/getInvoice', {
        type: selectedPosType,
        billId,
      })
      .then((res) => res.data)
      .then((res) => {
        if (res.success) {
          setBillInfo(res.billInfo);
          setBillItemsInfo(res.billItemsInfo);
          setEinvInfo(res.eInvInfo);
        } else {
          alert(res.msg);
        }
      })
      .catch((err) => {
        console.log(err);
        alert(err.response.data);
      })
      .finally(() => setLoading(false));
  };

  const card = (
    <React.Fragment>
      <CardContent style={{ paddingBottom: '4px' }}>
        <Grid container direction="column" spacing={1}>
          <Grid item lg={12}>
            <Typography
              gutterBottom
              component="div"
              style={{ fontSize: '18px' }}
            >
              Bill #:{' '}
              <span style={{ color: '#eb2f06' }}>{billInfo?.bill_id}</span>
            </Typography>
          </Grid>

          <Grid item lg={12}>
            <Typography
              gutterBottom
              component="div"
              style={{ fontSize: '18px' }}
            >
              Date:{' '}
              <span style={{ color: '#eb2f06' }}>{billInfo?.bill_date}</span>
            </Typography>
          </Grid>
        </Grid>
      </CardContent>

      {einvInfo?.irn_info ? (
        <Grid
          container
          direction="column"
          spacing={1}
          style={{ marginLeft: '12px' }}
        >
          <Grid item lg={12}>
            <Typography
              gutterBottom
              component="div"
              style={{ fontSize: '18px' }}
            >
              ACK #:
              <span style={{ color: '#eb2f06' }}>
                {einvInfo?.irn_info?.ackNo}
              </span>
            </Typography>
          </Grid>

          <Grid item lg={12}>
            <Typography
              gutterBottom
              component="div"
              style={{ fontSize: '18px' }}
            >
              Status:{' '}
              <span
                style={{
                  color: einvInfo?.is_cancelled ? '#eb2f06' : '#006266',
                }}
              >
                {einvInfo?.is_cancelled ? 'CANCELLED' : 'COMPLETE'}
              </span>
            </Typography>
          </Grid>

          <CardActions
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PrimaryButton
              onClick={async (_: any) => {
                try {
                  await downloadPosInvoice(billId);
                } catch (err: any) {
                  console.log(err);
                  if (err && err?.response && err?.response?.data) {
                    alert(err?.response?.data);
                  } else {
                    alert('Unknown error while downloading POS E-Invoice!');
                  }
                }
              }}
              style={{
                alignSelf: 'center',
                minWidth: '300px',
                justifyContent: 'center',
              }}
              className={classes.confirmButton}
              color="secondary"
            >
              DOWNLOAD
            </PrimaryButton>
          </CardActions>
        </Grid>
      ) : (
        <CardContent style={{ paddingTop: '4px' }}>
          <Grid container direction="column" spacing={1}>
            <Grid item lg={12} direction="row">
              <FormControlLabel
                style={{ fontSize: '18px' }}
                control={
                  <Checkbox
                    checked={partyHeldOnDateEnabled}
                    onChange={(e) => {
                      setPartyHeldOnDateEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setPartyHeldDate(null);
                      }
                    }}
                    name="party-date"
                  />
                }
                label="Party Date:"
              />
              {partyHeldOnDateEnabled ? (
                <DatePicker
                  selected={partyHeldDate}
                  className={classes.datePicker}
                  onChange={(date) => {
                    if (date != null && !Array.isArray(date)) {
                      setPartyHeldDate(date);
                    }
                  }}
                  selectsRange={false}
                  maxDate={moment(billInfo.bill_date, 'DD-MM-YYYY')
                    .subtract({ days: 1 })
                    .toDate()}
                  minDate={moment(billInfo.bill_date, 'DD-MM-YYYY')
                    .subtract({ days: 20 })
                    .toDate()}
                  dateFormat="dd-MM-yyyy"
                />
              ) : (
                []
              )}
            </Grid>

            <Grid item lg={12}>
              <Typography
                gutterBottom
                component="div"
                style={{ fontSize: '18px' }}
              >
                GSTIN #:{' '}
                <span style={{ color: '#eb2f06' }}>
                  {selectedCompany ? selectedCompany[0]?.gstin : ''}
                </span>
              </Typography>
            </Grid>

            <Grid item lg={12}>
              <Typography
                gutterBottom
                component="div"
                style={{ fontSize: '18px' }}
              >
                Taxpayer Type:{' '}
                <span style={{ color: '#eb2f06' }}>
                  {selectedCompany ? selectedCompany[0]?.taxpayer_type : ''}
                </span>
              </Typography>
            </Grid>
          </Grid>

          <Multiselect
            placeholder="Select Company Name"
            style={{
              marginTop: '24px',
              minWidth: '300px',
              maxWidth: '500px',
            }}
            data={gstMappings}
            groupBy={(info) => info.state}
            textField={(info: any) => info.legal_name}
            value={selectedCompany}
            onChange={(r) => {
              if (r.length === 1) {
                setSelectedCompany(r);
              } else if (r.length === 0) {
                setSelectedCompany([]);
              }
            }}
            renderListGroup={({ group }) => {
              return (
                <span
                  style={{
                    color: '#ff1757',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  {group}
                </span>
              );
            }}
            renderTagValue={({ item }) => {
              return (
                <span
                  style={{
                    color: 'black',
                    fontWeight: 400,
                  }}
                >
                  {item.legal_name}
                </span>
              );
            }}
            renderListItem={({ item }) => {
              return (
                <span
                  style={{
                    color: 'black',
                    fontWeight: 400,
                    fontSize: '14px',
                  }}
                >
                  {item.legal_name}
                </span>
              );
            }}
          />
        </CardContent>
      )}

      {!einvInfo?.irn_info && billItemsInfo && billItemsInfo.length === 1 ? (
        <CardActions
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SecondaryButton
            onClick={async (_: any) => {
              if (selectedCompany && selectedCompany.length > 0 && billInfo) {
                const gstin = selectedCompany[0]?.gstin;
                const partyHeldOnDate = partyHeldDate;
                try {
                  const generateRes = await axios.post(
                    '/api/pos/generateEInvoice',
                    {
                      type: selectedPosType,
                      billId: billInfo.bill_id,
                      gstin,
                      partyHeldOnDate,
                    }
                  );

                  const posEInvInfo = generateRes.data;
                  if (posEInvInfo && posEInvInfo.success) {
                    await downloadPosInvoice(posEInvInfo.billId);
                    resetComponent();
                  } else {
                    alert('Error generating E-Invoice!');
                  }
                } catch (err: any) {
                  console.log(err);
                  if (err && err?.response && err?.response?.data) {
                    alert(err?.response?.data);
                  } else {
                    alert('Unknown error while generating POS E-Invoice!');
                  }
                }
              } else {
                alert('Missing required information!');
              }
            }}
            style={{
              alignSelf: 'center',
              minWidth: '300px',
              justifyContent: 'center',
            }}
            className={classes.confirmButton}
            color="secondary"
          >
            CONFIRM
          </SecondaryButton>
        </CardActions>
      ) : (
        []
      )}
    </React.Fragment>
  );

  const getEInvoiceContainer = () => {
    if (billInfo && billItemsInfo && billItemsInfo.length > 0) {
      return (
        <Card
          variant="outlined"
          style={{ flex: 0.6 }}
          className={classes.invoiceDetailsContainer}
        >
          {card}
        </Card>
      );
    }
  };

  const getPosBillTable = () => {
    if (billInfo && billItemsInfo && billItemsInfo.length > 0) {
      const StyledTableCell = withStyles((theme) => ({
        head: {
          backgroundColor: theme.palette.common.black,
          color: theme.palette.common.white,
        },
        body: {
          fontSize: 16,
        },
      }))(TableCell);

      const StyledTableRow = withStyles((theme) => ({
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }))(TableRow);

      const billSummary = () => (
        <>
          <StyledTableRow key={'subTotal'}>
            <StyledTableCell colSpan={4} align="center">
              --------------------------------------------
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow key={'subTotal'}>
            <StyledTableCell colSpan={3} style={{ fontWeight: 800 }}>
              SUB TOTAL
            </StyledTableCell>
            <StyledTableCell align="right" style={{ fontWeight: 800 }}>
              {toINR(billInfo?.bill_total)}
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow key={'cgst'}>
            <StyledTableCell colSpan={3} style={{ fontWeight: 800 }}>
              C - GST
            </StyledTableCell>
            <StyledTableCell align="right" style={{ fontWeight: 800 }}>
              {toINR(billInfo?.cgst_amount)}
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow key={'sgst'}>
            <StyledTableCell colSpan={3} style={{ fontWeight: 800 }}>
              S - GST
            </StyledTableCell>
            <StyledTableCell align="right" style={{ fontWeight: 800 }}>
              {toINR(billInfo?.sgst_amount)}
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow key={'total'}>
            <StyledTableCell colSpan={3} style={{ fontWeight: 800 }}>
              TOTAL (Rs)
            </StyledTableCell>
            <StyledTableCell align="right" style={{ fontWeight: 800 }}>
              {toINR(billInfo?.grand_total)}
            </StyledTableCell>
          </StyledTableRow>
        </>
      );

      return (
        <TableContainer
          style={{ flex: 1 }}
          component={Paper}
          className={classes.particularsTable}
        >
          <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">ITEM NAME</StyledTableCell>
                <StyledTableCell align="right">RATE</StyledTableCell>
                <StyledTableCell align="right">QUANTITY</StyledTableCell>
                <StyledTableCell align="right">TOTAL</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billItemsInfo.map((row) => (
                <StyledTableRow key={row?.item_name}>
                  <StyledTableCell width={'55%'}>
                    {row?.item_name}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'15%'}>
                    {row.rate}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'15%'}>
                    {row.quantity}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'15%'}>
                    {row.total}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
              {billSummary()}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return [];
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>POS E-Invoice</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.posTypeGroup}>
          {Array.from(posTypes.keys()).map((type) => (
            <div
              key={type}
              style={{
                marginRight: '24px',
                minWidth: '88px',
                marginTop: '18px',
                marginBottom: '18px',
              }}
            >
              <input
                style={{
                  height: '18px',
                  width: '18px',
                  margin: 0,
                  verticalAlign: 'middle',
                }}
                name="pos-type"
                type="radio"
                id={type}
                value={type}
                checked={selectedPosType === type}
                onChange={(e) => {
                  setSelectedPosType(e.target.value);
                  setBillId('');
                  setBillInfo(undefined);
                  setBillItemsInfo([]);
                }}
              />
              <label> {posTypes.get(type)} </label>
            </div>
          ))}
        </div>
        <div className={classes.container}>
          <div style={{ marginTop: '8px', marginBottom: '24px' }}>
            <input
              style={{ height: '32px', fontSize: '22px' }}
              name="pos_bill_id"
              id="pos_bill_id"
              placeholder="Invoice No"
              onChange={(e) => {
                setBillId(e.target.value);
                setBillInfo(undefined);
                setBillItemsInfo([]);
              }}
              value={billId || ''}
            />
          </div>
          <div className={classes.buttonContainer}>
            <PrimaryButton
              style={{ maxWidth: '300px', width: '100%' }}
              onClick={() => getBillInfo()}
            >
              FETCH
            </PrimaryButton>
          </div>
          <hr className={classes.separatorLine} />
          <div className={classes.infoContainer}>
            {getPosBillTable()}
            {getEInvoiceContainer()}
          </div>
        </div>
      </div>
    );
  }
};

export default RestaurantInvoice;
